import { styled, alpha } from '@mui/material/styles';
import { Box, Card, Button, TextField } from '@mui/material';

export const OrderSummaryCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.12) : '#eff6ff',
  borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.35) : '#bfdbfe',
  border: '1px solid',
}));

export const InfoHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

export const SummaryRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
});

export const CustomAmountCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

export const CustomAmountBox = styled(Box)<{ isActive: boolean }>(({ theme, isActive }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  border: '1px solid',
  borderColor: isActive ? theme.palette.warning.main : theme.palette.divider,
  backgroundColor: isActive
    ? theme.palette.mode === 'dark'
      ? alpha(theme.palette.warning.main, 0.14)
      : '#fffbeb'
    : theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.04)
      : '#f9fafb',
  transition: 'all 0.15s ease',
  marginBottom: theme.spacing(2),
}));

export const CustomAmountHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const CustomAmountIconBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const AdjustmentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.06)
      : theme.palette.grey[100],
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

export const FinalRefundBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.success.main, 0.12) : '#f0fdf4',
  borderRadius: theme.spacing(1),
  border: '1px solid',
  borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.success.main, 0.4) : '#bbf7d0',
}));

export const FinalRefundRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const NotesTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '& fieldset': {
      borderColor: alpha(theme.palette.success.main, 0.45),
    },
    '&:hover fieldset': {
      borderColor: theme.palette.success.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0BB296',
      borderWidth: '2px',
    },
  },
}));

// Color/fondo/hover vienen de variant="tonal" del tema; aquí solo tamaño/spacing.
export const CancelButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  minWidth: '120px',
  padding: '9px 18px',
  borderRadius: '10px',
  fontSize: '0.875rem',
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
}));

export const SubmitButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  background: 'linear-gradient(to right, #06b6d4, #10b981)',
  color: 'white',
  '&:hover': {
    background: 'linear-gradient(to right, #0891b2, #059669)',
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    background: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
}));

export const DashboardCardBlue = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.12)
      : theme.palette.background.paper,
  border: `1px solid ${
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.35)
      : 'rgba(59, 130, 246, 0.22)'
  }`,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 2px 12px rgba(0, 0, 0, 0.35)'
      : '0 2px 8px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.2s ease',
  backgroundImage: 'none',
  '&:hover': {
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 4px 16px rgba(0, 0, 0, 0.45)'
        : '0 4px 12px rgba(0, 0, 0, 0.12)',
  },
}));

export const DashboardCardGreen = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.success.main, 0.1)
      : theme.palette.background.paper,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 2px 12px rgba(0, 0, 0, 0.35)'
      : '0 2px 8px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.2s ease',
  backgroundImage: 'none',
  '&:hover': {
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 4px 16px rgba(0, 0, 0, 0.45)'
        : '0 4px 12px rgba(0, 0, 0, 0.12)',
  },
}));

export const DashboardCardRed = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.error.main, 0.12)
      : theme.palette.background.paper,
  border: `1px solid ${
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.error.main, 0.35)
      : 'rgba(239, 68, 68, 0.22)'
  }`,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 2px 12px rgba(0, 0, 0, 0.35)'
      : '0 2px 8px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.2s ease',
  backgroundImage: 'none',
  '&:hover': {
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 4px 16px rgba(0, 0, 0, 0.45)'
        : '0 4px 12px rgba(0, 0, 0, 0.12)',
  },
}));

/** Tarjeta principal del reembolso (jerarquía visual sobre el resto). */
export const HeroRefundCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.success.main, 0.14)
      : theme.palette.background.paper,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 4px 24px rgba(0, 0, 0, 0.4)'
      : '0 4px 20px rgba(16, 185, 129, 0.12)',
  border: `1px solid ${
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.success.main, 0.42)
      : 'rgba(16, 185, 129, 0.2)'
  }`,
  transition: 'box-shadow 0.2s ease',
  backgroundImage: 'none',
  '&:hover': {
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 6px 28px rgba(0, 0, 0, 0.5)'
        : '0 6px 24px rgba(16, 185, 129, 0.16)',
  },
}));
